import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scale, ShoppingCart, X, Trash2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCompare } from '../context/CompareContext';
import { useCart } from '../context/CartContext';
import { getProxiedImageUrl, getProductPath } from '../utils/helpers';

const Compare = () => {
  const { t, language } = useLanguage();
  const { compareList, removeFromCompare, clearCompare, maxCompare } = useCompare();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  if (compareList.length === 0) {
    return (
      <div className="dark-container pt-20">
        <section className="py-20 px-6 lg:px-[7.6923%]">
          <div className="max-w-[1400px] mx-auto text-center">
            <div className="mb-8">
              <Scale size={80} className="mx-auto mb-6 text-white/20" />
              <h1 className="display-large mb-4">
                {language === 'it' ? 'Nessun Prodotto in Confronto' : 'No Products to Compare'}
              </h1>
              <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                {language === 'it' 
                  ? `Aggiungi fino a ${maxCompare} prodotti per confrontarli fianco a fianco.` 
                  : `Add up to ${maxCompare} products to compare them side by side.`}
              </p>
              <Link to="/products" className="btn-primary inline-flex items-center gap-2">
                {language === 'it' ? 'Esplora Prodotti' : 'Browse Products'}
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Comparison attributes
  const comparisonRows = [
    { 
      label: language === 'it' ? 'Immagine' : 'Image', 
      key: 'image',
      render: (product) => (
        <div className="aspect-square overflow-hidden bg-white">
          {product.images && product.images.length > 0 ? (
            <img
              src={getProxiedImageUrl(product.images[0])}
              alt={language === 'it' ? product.name_it : product.name_en}
              className="w-full h-full object-contain p-4"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20">
              {language === 'it' ? 'No Image' : 'No Image'}
            </div>
          )}
        </div>
      )
    },
    { 
      label: language === 'it' ? 'Nome' : 'Name', 
      key: 'name',
      render: (product) => (
        <div className="font-semibold text-lg">
          {language === 'it' ? product.name_it : product.name_en}
        </div>
      )
    },
    { 
      label: language === 'it' ? 'Prezzo' : 'Price', 
      key: 'price',
      render: (product) => (
        <div className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>
          €{product.price ? product.price.toFixed(2) : '0.00'}
        </div>
      )
    },
    { 
      label: language === 'it' ? 'Disponibilità' : 'Availability', 
      key: 'stock',
      render: (product) => (
        product.stock > 0 ? (
          <div className="text-sm px-3 py-1 bg-green-500/20 text-green-400 inline-block">
            {language === 'it' ? 'Disponibile' : 'In Stock'}
          </div>
        ) : (
          <div className="text-sm px-3 py-1 bg-red-500/20 text-red-400 inline-block">
            {language === 'it' ? 'Esaurito' : 'Out of Stock'}
          </div>
        )
      )
    },
    { 
      label: language === 'it' ? 'Categoria' : 'Category', 
      key: 'category',
      render: (product) => (
        <div style={{ color: 'var(--brand-primary)' }}>
          {product.category || '-'}
        </div>
      )
    },
    { 
      label: language === 'it' ? 'Marca' : 'Brand', 
      key: 'brand',
      render: (product) => (
        <div className="font-semibold">
          {product.brand || '-'}
        </div>
      )
    },
    { 
      label: 'SKU', 
      key: 'sku',
      render: (product) => (
        <div className="text-sm text-white/60">
          {product.sku}
        </div>
      )
    },
    { 
      label: language === 'it' ? 'Peso' : 'Weight', 
      key: 'weight',
      render: (product) => (
        <div>
          {product.weight ? `${product.weight} kg` : '-'}
        </div>
      )
    },
  ];

  return (
    <div className="dark-container pt-20">
      {/* Page Header */}
      <section className="py-20 px-6 lg:px-[7.6923%]" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="display-large mb-4">
                {language === 'it' ? 'Confronta Prodotti' : 'Compare Products'}
              </h1>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {language === 'it' 
                  ? `${compareList.length} di ${maxCompare} prodotti` 
                  : `${compareList.length} of ${maxCompare} products`}
              </p>
            </div>
            {compareList.length > 0 && (
              <button
                onClick={clearCompare}
                className="flex items-center gap-2 px-6 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <Trash2 size={20} />
                {language === 'it' ? 'Rimuovi Tutti' : 'Clear All'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border border-white/10 p-4 text-left bg-white/5" style={{ minWidth: '200px' }}>
                  {language === 'it' ? 'Caratteristica' : 'Feature'}
                </th>
                {compareList.map((product) => (
                  <th key={product.sku} className="border border-white/10 p-4 text-center bg-white/5 relative" style={{ minWidth: '280px' }}>
                    <button
                      onClick={() => removeFromCompare(product.sku)}
                      className="absolute top-2 right-2 p-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full transition-colors z-10"
                      title={language === 'it' ? 'Rimuovi dal confronto' : 'Remove from comparison'}
                    >
                      <X size={16} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, index) => (
                <tr key={row.key} className={index % 2 === 0 ? 'bg-white/5' : ''}>
                  <td className="border border-white/10 p-4 font-semibold">
                    {row.label}
                  </td>
                  {compareList.map((product) => (
                    <td key={product.sku} className="border border-white/10 p-4 text-center">
                      {row.render(product)}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Action Row */}
              <tr className="bg-white/10">
                <td className="border border-white/10 p-4 font-semibold">
                  {language === 'it' ? 'Azioni' : 'Actions'}
                </td>
                {compareList.map((product) => (
                  <td key={product.sku} className="border border-white/10 p-4 text-center">
                    <div className="flex flex-col gap-2">
                      <button 
                        className={`w-full py-2 px-4 rounded font-semibold transition-all flex items-center justify-center gap-2 ${
                          product.stock === 0 
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                            : 'btn-primary'
                        }`}
                        disabled={product.stock === 0}
                        onClick={() => {
                          if (product.stock > 0) {
                            addToCart(product);
                          }
                        }}
                      >
                        <ShoppingCart size={18} />
                        {product.stock === 0 
                          ? (language === 'it' ? 'Esaurito' : 'Out of Stock')
                          : (language === 'it' ? 'Aggiungi' : 'Add to Cart')
                        }
                      </button>
                      <Link
                        to={getProductPath(product)}
                        className="w-full py-2 px-4 rounded font-semibold transition-all text-center btn-secondary"
                      >
                        {language === 'it' ? 'Dettagli' : 'View Details'}
                      </Link>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* Continue Shopping */}
          <div className="text-center mt-12">
            <Link to="/products" className="btn-secondary inline-flex items-center gap-2">
              {language === 'it' ? 'Continua lo Shopping' : 'Continue Shopping'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compare;
